import { Outlet, useRouterState } from "@tanstack/react-router";
import { MotionConfig } from "framer-motion";
import { Shell } from "@/components/layout/Shell";
import { useAuth } from "@/hooks/useAuth";

/** Root layout — decides whether to render the full Shell chrome or
 * a bare Outlet (used for /login which brings its own centered layout).
 * Also kicks off the auth query at the top so every downstream route
 * implicitly waits for the user identity.
 *
 * The whole tree is wrapped in <MotionConfig reducedMotion="user"> so
 * framer-motion honours the OS-level `prefers-reduced-motion` setting
 * without any extra work at the call site. */
export function RootLayout() {
  const { pathname } = useRouterState({ select: (s) => s.location });
  const isLogin = pathname === "/login";
  const auth = useAuth();

  let content;
  if (isLogin) {
    content = <Outlet />;
  } else if (auth.isPending || auth.error) {
    // While auth resolves on a protected route, let the skeleton inside
    // the Shell take over. If auth errored, useAuth will have navigated
    // already.
    content = (
      <div className="mx-auto max-w-[1400px] px-8 pb-12 pt-4">
        <div className="skeleton h-16" />
        <div className="mt-4 skeleton h-20" />
        <div className="mt-4 skeleton h-10" />
      </div>
    );
  } else {
    content = <Shell />;
  }

  return <MotionConfig reducedMotion="user">{content}</MotionConfig>;
}
